"use client";

import Image from "next/image";
import { CloudArrowUp } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { Link } from "@/navigation";

interface IndustryHeroProps {
	namespace: string;
	image: string;
	altKey: string;
	brandColor?: string;
	darkBrandColor?: string;
	uploadOpensModal?: boolean;
	titleClassName?: string;
}

export default function IndustryHero({
	namespace,
	image,
	altKey,
	brandColor = "#64a525",
	darkBrandColor = "#4e8a1c",
	uploadOpensModal = false,
	titleClassName = "",
}: IndustryHeroProps) {
	const t = useTranslations(namespace);
	const { openModalInquiry } = useModalInquiryContext();

	return (
		<section className="relative w-full overflow-hidden">
			<Image src={image} alt={t(altKey)} fill className="object-cover" sizes="100vw" priority />
			<div className="absolute inset-0 bg-gradient-to-r from-[#fefeff] via-[#fefeff]/70 to-transparent" />
			<div className="absolute bottom-0 left-0 h-[200px] w-full [background:linear-gradient(to_bottom_left,transparent_60%,#ecfed4_100%)]" />

			<div className="container relative z-10 py-5 sm:pb-7 sm:pt-6 md:pb-8 md:pt-7 lg:pb-[50px] lg:pt-[65px]">
				<div className="mx-auto max-w-3xl lg:mx-0 lg:max-w-[80%]">
					<div className="-ml-[16px] inline-block">
						<div
							className="trustpilot-widget scale-125"
							data-locale="en-US"
							data-template-id="5419b637fa0340045cd0c936"
							data-businessunit-id="68808142dd0a4c4d3e411b9c"
							data-style-height="24px"
							data-style-width="100%"
						>
							<a
								href="https://www.trustpilot.com/review/www.cncprotolabs.com?utm_medium=trustbox&utm_source=MicroTrustScore"
								target="_blank"
								rel="noopener"
							>
								{t("trustpilotFallback")}
							</a>
						</div>
					</div>

					<p className="mb-2 mt-3 text-xs font-medium tracking-wide text-black/70 sm:text-sm">
						{t("bannerBreadcrumb")}
					</p>
					<h1
						className={`mb-5 font-sans text-[26px] font-extrabold leading-snug text-black sm:text-4xl sm:leading-tight md:mb-6 md:text-[42px] md:leading-tight lg:text-5xl lg:leading-tight ${titleClassName}`}
					>
						{t("bannerTitle")}
					</h1>
					<p className="mb-6 max-w-[90%] text-sm leading-relaxed text-black/80 md:mb-8 md:text-base">
						{t("bannerDesc")}
					</p>

					<div className="mb-8 flex flex-col gap-3 sm:flex-row sm:gap-4 md:mb-10">
						{uploadOpensModal ? (
							<>
								<button
									onClick={openModalInquiry}
									className="group flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:text-white hover:shadow-xl sm:w-auto sm:px-6 sm:py-3.5 md:text-base"
									style={{ backgroundColor: brandColor, boxShadow: `0 10px 20px ${brandColor}33` }}
								>
									<CloudArrowUp className="h-5 w-5" weight="fill" />
									{t("bannerUploadCad")}
								</button>
								<Link
									href="/upload"
									className="flex w-full items-center justify-center rounded-md border-2 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:text-white hover:shadow-xl sm:w-auto sm:px-6 sm:py-3.5 md:text-base"
									style={{ backgroundColor: darkBrandColor, borderColor: darkBrandColor }}
								>
									{t("bannerGetQuote")}
								</Link>
							</>
						) : (
							<>
								<Link
									href="/upload"
									target="_blank"
									className="group flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:text-white hover:shadow-xl sm:w-auto sm:px-6 sm:py-3.5 md:text-base"
									style={{ backgroundColor: brandColor, boxShadow: `0 10px 20px ${brandColor}33` }}
								>
									<CloudArrowUp className="h-5 w-5" weight="fill" />
									{t("bannerUploadCad")}
								</Link>
								<button
									onClick={openModalInquiry}
									className="flex w-full items-center justify-center rounded-md border-2 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:w-auto sm:px-6 sm:py-3.5 md:text-base"
									style={{ backgroundColor: darkBrandColor, borderColor: darkBrandColor }}
								>
									{t("bannerGetQuote")}
								</button>
							</>
						)}
					</div>

					<div>
						<p className="mb-1 text-sm font-semibold md:text-base" style={{ color: brandColor }}>
							{t("bannerCertTitle")}
						</p>
						<p className="text-xs text-black/70 sm:text-sm">{t("bannerCertList")}</p>
					</div>
				</div>
			</div>
		</section>
	);
}
